import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Lock, ArrowRight, Sparkles, Users, Calendar, MessageCircle, Heart, User, ArrowLeft, KeyRound } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../context/AuthContext';
import { register, login, checkEmail, resetPassword } from '../lib/api';
import { toast } from 'sonner';

export default function AuthPage() {
  const [mode, setMode] = useState('welcome');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [age, setAge] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const { login: authLogin } = useAuth();
  const navigate = useNavigate();

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!firstName.trim() || !lastName.trim()) {
      toast.error('Please enter your first and last name');
      return;
    }
    
    const ageNum = parseInt(age);
    if (!age || isNaN(ageNum) || ageNum < 1) {
      toast.error('Please enter a valid age');
      return;
    }
    
    if (ageNum < 18) {
      toast.error('You must be 18 or older to create an account');
      return;
    }
    
    if (!agreedToTerms) {
      toast.error('Please confirm that the information provided is accurate');
      return;
    }
    
    if (!email.endsWith('@hw.ac.uk')) {
      toast.error('Only @hw.ac.uk emails are allowed');
      return;
    }
    
    setLoading(true);
    try {
      const res = await register(email, password, firstName, lastName, ageNum);
      authLogin(res.data.token, res.data);
      toast.success('Welcome to HW Vibes!');
      navigate('/setup');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    }
    setLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login(email, password);
      authLogin(res.data.token, res.data);
      toast.success('Welcome back!');
      navigate('/feed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    }
    setLoading(false);
  };

  const handleCheckEmail = async (e) => {
    e.preventDefault();
    if (!resetEmail.endsWith('@hw.ac.uk')) {
      toast.error('Please enter a valid @hw.ac.uk email');
      return;
    }
    setLoading(true);
    try {
      await checkEmail(resetEmail);
      setMode('reset-password');
      toast.success('Email found! Create your new password.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Email not found. Please sign up first.');
    }
    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const res = await resetPassword(resetEmail, newPassword);
      authLogin(res.data.token, res.data);
      toast.success('Password reset successful! Welcome back!');
      navigate('/feed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Password reset failed');
    }
    setLoading(false);
  };

  const features = [
    { icon: Calendar, title: 'Discover Events', desc: 'Find campus vibes that match yours', color: 'text-pink-500' },
    { icon: Users, title: 'Connect', desc: 'Meet students with shared interests', color: 'text-blue-500' },
    { icon: MessageCircle, title: 'Chat', desc: 'Join groups and make plans', color: 'text-purple-500' },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <AnimatePresence mode="wait">
        {mode === 'welcome' && (
          <motion.div
            key="welcome"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex-1 flex flex-col"
          >
            <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 relative overflow-hidden">
              {/* Decorative circles */}
              <div className="absolute top-20 left-10 w-32 h-32 bg-pink-200 rounded-full blur-3xl opacity-60" />
              <div className="absolute bottom-40 right-10 w-40 h-40 bg-blue-200 rounded-full blur-3xl opacity-60" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-purple-100 rounded-full blur-3xl opacity-40" />
              
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="relative z-10 text-center max-w-md"
              >
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-pink-100 border border-pink-200 mb-6">
                  <Sparkles className="w-4 h-4 text-pink-500" />
                  <span className="text-sm font-medium text-pink-600">Heriot-Watt Exclusive</span>
                </div>
                
                <h1 className="text-5xl sm:text-6xl font-black tracking-tight mb-2 text-pink-500">
                  HW Vibes
                </h1>
                <p className="text-lg text-gray-500 mb-2 flex items-center justify-center gap-2">
                  <Heart className="w-5 h-5 text-pink-500 fill-pink-500" />
                  Campus Life, Amplified
                </p>
                
                <p className="text-gray-600 mb-8">
                  Swipe through events. Connect with students. Experience university like never before.
                </p>

                <div className="grid gap-3 mb-8">
                  {features.map((f, i) => (
                    <motion.div
                      key={f.title}
                      initial={{ x: -20, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: 0.2 + i * 0.1 }}
                      className="flex items-center gap-4 p-4 rounded-2xl bg-white/80 backdrop-blur-sm border border-gray-100 shadow-sm text-left card-hover"
                    >
                      <div className={`p-2.5 rounded-xl bg-gradient-to-br from-pink-50 to-blue-50 ${f.color}`}>
                        <f.icon className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-800">{f.title}</h3>
                        <p className="text-sm text-gray-500">{f.desc}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <div className="flex flex-col gap-3">
                  <Button
                    data-testid="get-started-btn"
                    size="lg"
                    className="w-full h-14 text-lg font-bold rounded-2xl bg-pink-500 hover:bg-pink-600 border-0"
                    onClick={() => setMode('register')}
                  >
                    Get Started
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                  <Button
                    data-testid="login-btn"
                    variant="outline"
                    size="lg"
                    className="w-full h-14 text-lg rounded-2xl border-2 border-gray-200 hover:border-pink-300 hover:bg-pink-50"
                    onClick={() => setMode('login')}
                  >
                    I already have an account
                  </Button>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}

        {mode === 'register' && (
          <motion.div
            key="register"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="flex-1 flex items-center justify-center p-6 overflow-y-auto"
          >
            <Card className="w-full max-w-md border-0 shadow-xl bg-white my-4">
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-2xl font-bold text-pink-500">Join HW Vibes</CardTitle>
                <CardDescription>Create your account</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleRegister} className="space-y-4">
                  {/* Name Fields */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                        <Input
                          id="firstName"
                          data-testid="register-firstname-input"
                          type="text"
                          placeholder="John"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          className="pl-10 h-12 rounded-xl border-gray-200 focus:border-pink-400 focus:ring-pink-400"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name</Label>
                      <Input
                        id="lastName"
                        data-testid="register-lastname-input"
                        type="text"
                        placeholder="Doe"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="h-12 rounded-xl border-gray-200 focus:border-pink-400 focus:ring-pink-400"
                        required
                      />
                    </div>
                  </div>
                  
                  {/* Age Field */}
                  <div className="space-y-2">
                    <Label htmlFor="age">Age</Label>
                    <Input
                      id="age"
                      data-testid="register-age-input"
                      type="number"
                      placeholder="Your age (must be 18+)"
                      value={age}
                      onChange={(e) => setAge(e.target.value)}
                      className="h-12 rounded-xl border-gray-200 focus:border-pink-400 focus:ring-pink-400"
                      min="1"
                      max="120"
                      required
                    />
                    <p className="text-xs text-gray-500">You must be 18 or older to register</p>
                  </div>

                  {/* Email Field */}
                  <div className="space-y-2">
                    <Label htmlFor="email">University Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                      <Input
                        id="email"
                        data-testid="register-email-input"
                        type="email"
                        placeholder="your.name@hw.ac.uk"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10 h-12 rounded-xl border-gray-200 focus:border-pink-400 focus:ring-pink-400"
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                      <Input
                        id="password"
                        data-testid="register-password-input"
                        type="password"
                        placeholder="At least 6 characters"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 h-12 rounded-xl border-gray-200 focus:border-pink-400 focus:ring-pink-400"
                        required
                        minLength={6}
                      />
                    </div>
                  </div>
                  
                  {/* Terms & Responsibility Checkbox */}
                  <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
                    <Checkbox
                      id="terms"
                      data-testid="register-terms-checkbox"
                      checked={agreedToTerms}
                      onCheckedChange={(checked) => setAgreedToTerms(checked)}
                      className="mt-0.5 data-[state=checked]:bg-pink-500 data-[state=checked]:border-pink-500"
                    />
                    <label
                      htmlFor="terms"
                      className="text-sm text-gray-600 leading-relaxed cursor-pointer"
                    >
                      I confirm that all information provided is true and accurate. I take full responsibility for the information declared by me and understand that providing false information may result in account termination.
                    </label>
                  </div>

                  <Button
                    data-testid="register-submit-btn"
                    type="submit"
                    className="w-full h-12 font-bold rounded-xl bg-pink-500 hover:bg-pink-600 border-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={loading || !agreedToTerms}
                  >
                    {loading ? 'Creating account...' : 'Create Account'}
                  </Button>
                  <p className="text-center text-sm text-gray-500">
                    Already have an account?{' '}
                    <button
                      type="button"
                      className="text-pink-500 hover:text-pink-600 font-medium"
                      onClick={() => setMode('login')}
                    >
                      Sign in
                    </button>
                  </p>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {mode === 'login' && (
          <motion.div
            key="login"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="flex-1 flex items-center justify-center p-6"
          >
            <Card className="w-full max-w-md border-0 shadow-xl bg-white">
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-2xl font-bold text-pink-500">Welcome Back</CardTitle>
                <CardDescription>Sign in to continue</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                      <Input
                        id="login-email"
                        data-testid="login-email-input"
                        type="email"
                        placeholder="your.name@hw.ac.uk"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10 h-12 rounded-xl border-gray-200 focus:border-pink-400 focus:ring-pink-400"
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                      <Input
                        id="login-password"
                        data-testid="login-password-input"
                        type="password"
                        placeholder="Your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 h-12 rounded-xl border-gray-200 focus:border-pink-400 focus:ring-pink-400"
                        required
                      />
                    </div>
                  </div>
                  <Button
                    data-testid="login-submit-btn"
                    type="submit"
                    className="w-full h-12 font-bold rounded-xl bg-pink-500 hover:bg-pink-600 border-0"
                    disabled={loading}
                  >
                    {loading ? 'Signing in...' : 'Sign In'}
                  </Button>
                  <div className="text-center space-y-2">
                    <button
                      type="button"
                      className="text-blue-500 hover:text-blue-600 font-medium text-sm"
                      onClick={() => {
                        setResetEmail('');
                        setMode('forgot-password');
                      }}
                    >
                      Forgot password?
                    </button>
                    <p className="text-sm text-gray-500">
                      Don&apos;t have an account?{' '}
                      <button
                        type="button"
                        className="text-pink-500 hover:text-pink-600 font-medium"
                        onClick={() => setMode('register')}
                      >
                        Sign up
                      </button>
                    </p>
                  </div>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Forgot Password - Enter Email */}
        {mode === 'forgot-password' && (
          <motion.div
            key="forgot-password"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="flex-1 flex items-center justify-center p-6"
          >
            <Card className="w-full max-w-md border-0 shadow-xl bg-white">
              <CardHeader className="text-center pb-2">
                <button 
                  type="button"
                  className="absolute left-4 top-4 text-gray-400 hover:text-gray-600"
                  onClick={() => setMode('login')}
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="mx-auto w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mb-4">
                  <KeyRound className="w-8 h-8 text-blue-500" />
                </div>
                <CardTitle className="text-2xl font-bold text-blue-500">Reset Password</CardTitle>
                <CardDescription>Enter your email to reset your password</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCheckEmail} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="reset-email">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                      <Input
                        id="reset-email"
                        data-testid="reset-email-input"
                        type="email"
                        placeholder="your.name@hw.ac.uk"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        className="pl-10 h-12 rounded-xl border-gray-200 focus:border-blue-400 focus:ring-blue-400"
                        required
                      />
                    </div>
                  </div>
                  <Button
                    data-testid="check-email-btn"
                    type="submit"
                    className="w-full h-12 font-bold rounded-xl bg-blue-500 hover:bg-blue-600 border-0"
                    disabled={loading}
                  >
                    {loading ? 'Checking...' : 'Continue'}
                  </Button>
                  <p className="text-center text-sm text-gray-500">
                    Remember your password?{' '}
                    <button
                      type="button"
                      className="text-pink-500 hover:text-pink-600 font-medium"
                      onClick={() => setMode('login')}
                    >
                      Sign in
                    </button>
                  </p>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Reset Password - Enter New Password */}
        {mode === 'reset-password' && (
          <motion.div
            key="reset-password"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="flex-1 flex items-center justify-center p-6"
          >
            <Card className="w-full max-w-md border-0 shadow-xl bg-white">
              <CardHeader className="text-center pb-2">
                <button 
                  type="button"
                  className="absolute left-4 top-4 text-gray-400 hover:text-gray-600"
                  onClick={() => setMode('forgot-password')}
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                  <Lock className="w-8 h-8 text-green-500" />
                </div>
                <CardTitle className="text-2xl font-bold text-green-500">Create New Password</CardTitle>
                <CardDescription>
                  Resetting password for<br />
                  <span className="font-semibold text-gray-700">{resetEmail}</span>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="new-password">New Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                      <Input
                        id="new-password"
                        data-testid="new-password-input"
                        type="password"
                        placeholder="At least 6 characters"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="pl-10 h-12 rounded-xl border-gray-200 focus:border-green-400 focus:ring-green-400"
                        minLength={6}
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirm Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                      <Input
                        id="confirm-password"
                        data-testid="confirm-password-input"
                        type="password"
                        placeholder="Re-enter your password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="pl-10 h-12 rounded-xl border-gray-200 focus:border-green-400 focus:ring-green-400"
                        minLength={6}
                        required
                      />
                    </div>
                  </div>
                  <Button
                    data-testid="reset-password-btn"
                    type="submit"
                    className="w-full h-12 font-bold rounded-xl bg-green-500 hover:bg-green-600 border-0"
                    disabled={loading || newPassword.length < 6 || newPassword !== confirmPassword}
                  >
                    {loading ? 'Resetting...' : 'Reset Password & Sign In'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
